import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles, mergeClasses, shorthands, tokens, Text, Spinner } from '@fluentui/react-components';
import {
  ArrowRepeatAllRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  CircleHintFilled,
  ArrowSyncFilled,
  SubtractCircleFilled,
} from '@fluentui/react-icons';
import type { RepetitionInfo } from '@/services/monitoringApi';
import { formatDuration } from '@/utils/dateTime';

const COMPACT_THRESHOLD = 20;

type IterationSelectorProps = {
  repetitions: RepetitionInfo[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  isLoading?: boolean | undefined;
  totalDuration?: number | undefined;
};

const useStyles = makeStyles({
  root: {
    position: 'relative',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // 3px purple gradient accent on the left edge (from the mockup)
  rootAccent: {
    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '3px',
      backgroundColor: tokens.colorPalettePurpleForeground2,
      opacity: 0.5,
      '@media (forced-colors: active)': {
        backgroundColor: 'Highlight',
        opacity: 1,
      },
    },
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalXS,
  },
  headLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    fontSize: '11px',
    lineHeight: 1.2,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPalettePurpleForeground2,
    '@media (forced-colors: active)': {
      color: 'Highlight',
    },
  },
  headLabelIcon: {
    fontSize: '12px',
  },
  headMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  rail: {
    display: 'flex',
    gap: '6px',
    alignItems: 'stretch',
    overflowX: 'auto',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '2px',
    paddingRight: '2px',
    scrollSnapType: 'x mandatory',
    flex: 1,
    minWidth: 0,
    scrollbarWidth: 'thin',
  },
  // Card (default mode)
  card: {
    flex: '0 0 52px',
    width: '52px',
    height: '44px',
    scrollSnapAlign: 'start',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '5px 8px 4px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    textAlign: 'left',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    fontFamily: 'inherit',
    transitionProperty: 'transform, background-color, border-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
      color: tokens.colorNeutralForeground1,
    },
    ':active': {
      transform: 'scale(0.96)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  cardSelected: {
    backgroundColor: tokens.colorBrandBackground,
    ...shorthands.borderColor(tokens.colorBrandBackground),
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
      ...shorthands.borderColor(tokens.colorBrandBackgroundHover),
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '@media (forced-colors: active)': {
      ...shorthands.borderColor('Highlight'),
      outlineWidth: '1px',
      outlineStyle: 'solid',
      outlineColor: 'Highlight',
    },
  },
  cardFailed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    ...shorthands.borderColor(tokens.colorPaletteRedBorder1),
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalXXS,
    lineHeight: 1,
  },
  cardIndex: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
    color: 'inherit',
  },
  cardStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    lineHeight: 1,
    flexShrink: 0,
  },
  cardStatusSucceeded: {
    color: tokens.colorPaletteGreenForeground1,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  cardStatusFailed: {
    color: tokens.colorPaletteRedForeground1,
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
  cardStatusPending: {
    color: tokens.colorPaletteYellowForeground1,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  cardStatusRunning: {
    color: tokens.colorBrandForeground1,
    '@media (forced-colors: active)': {
      color: 'Highlight',
    },
  },
  cardStatusSkipped: {
    color: tokens.colorNeutralForeground3,
    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
  cardStatusOnSelected: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
  cardDuration: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
  },
  cardDurationOnSelected: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
  // Compact mode card (28x28)
  cardCompact: {
    flex: '0 0 28px',
    width: '28px',
    height: '28px',
    padding: 0,
    borderRadius: tokens.borderRadiusSmall,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    // Pseudo-element extends hit area to >= 32x32 (28 + 4*2 = 36)
    '::after': {
      content: '""',
      position: 'absolute',
      top: '-4px',
      right: '-4px',
      bottom: '-4px',
      left: '-4px',
    },
  },
  cardCompactInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1px',
    width: '100%',
    height: '100%',
  },
  cardCompactIndex: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightMedium,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1,
    color: 'inherit',
  },
  cardCompactDot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  cardCompactDotSucceeded: {
    backgroundColor: tokens.colorPaletteGreenForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
    },
  },
  cardCompactDotFailed: {
    backgroundColor: tokens.colorPaletteRedForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'LinkText',
    },
  },
  cardCompactDotPending: {
    backgroundColor: tokens.colorPaletteYellowForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'CanvasText',
    },
  },
  cardCompactDotRunning: {
    backgroundColor: tokens.colorBrandForeground1,
    '@media (forced-colors: active)': {
      backgroundColor: 'Highlight',
    },
  },
  cardCompactDotSkipped: {
    backgroundColor: tokens.colorNeutralForeground3,
    '@media (forced-colors: active)': {
      backgroundColor: 'GrayText',
    },
  },
  cardCompactDotOnSelected: {
    backgroundColor: tokens.colorNeutralForegroundOnBrand,
  },
  // Pager
  pager: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '2px',
    height: '32px',
    flexShrink: 0,
    minWidth: '96px',
    justifyContent: 'space-between',
  },
  pagerBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: tokens.colorNeutralForeground2,
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.4,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  pagerPos: {
    padding: `0 ${tokens.spacingHorizontalXS}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  pagerJump: {
    width: '36px',
    height: '22px',
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: '11px',
    padding: `0 ${tokens.spacingHorizontalXXS}`,
    fontVariantNumeric: 'tabular-nums',
    margin: '0 2px',
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '1px',
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },
    // Hide spinner buttons - we have our own pager
    '::-webkit-outer-spin-button': { appearance: 'none', margin: 0 },
    '::-webkit-inner-spin-button': { appearance: 'none', margin: 0 },
    appearance: 'textfield',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

type StatusKind = 'succeeded' | 'failed' | 'running' | 'skipped' | 'pending';

const getStatusKind = (status: RepetitionInfo['status']): StatusKind => {
  switch (status) {
    case 'succeeded': return 'succeeded';
    case 'failed': return 'failed';
    case 'running': return 'running';
    case 'skipped': return 'skipped';
    default: return 'pending';
  }
};

const STATUS_LABEL_SUFFIX: Record<StatusKind, string> = {
  succeeded: '',
  failed: ', failed',
  running: ', running',
  skipped: ', skipped',
  pending: ', pending',
};

export const IterationSelector = ({
  repetitions,
  selectedIndex,
  onSelect,
  isLoading,
  totalDuration,
}: IterationSelectorProps) => {
  const styles = useStyles();
  const railRef = useRef<HTMLDivElement>(null);
  const jumpInputRef = useRef<HTMLInputElement>(null);
  const isCompact = repetitions.length >= COMPACT_THRESHOLD;
  const [jumpDraft, setJumpDraft] = useState<string>('');

  // Sync jump draft to selectedIndex, but only when the input is NOT focused.
  // Otherwise we'd overwrite whatever the user is typing on every re-render.
  useEffect(() => {
    if (document.activeElement !== jumpInputRef.current) {
      setJumpDraft(String(selectedIndex + 1));
    }
  }, [selectedIndex]);

  // Scroll selected card into view when it changes. Honor prefers-reduced-motion.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const selectedEl = rail.querySelector<HTMLElement>('[data-iter-selected="true"]');
    const prefersReduced = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    selectedEl?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  }, [selectedIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let nextIndex = selectedIndex;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = Math.min(selectedIndex + 1, repetitions.length - 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = Math.max(selectedIndex - 1, 0);
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = repetitions.length - 1;
      }
      if (nextIndex !== selectedIndex) {
        onSelect(nextIndex);
        const rail = railRef.current;
        const tabs = rail?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        tabs?.[nextIndex]?.focus();
      }
    },
    [selectedIndex, repetitions.length, onSelect]
  );

  const goPrev = useCallback(() => {
    if (selectedIndex > 0) onSelect(selectedIndex - 1);
  }, [selectedIndex, onSelect]);

  const goNext = useCallback(() => {
    if (selectedIndex < repetitions.length - 1) onSelect(selectedIndex + 1);
  }, [selectedIndex, repetitions.length, onSelect]);

  const commitJump = useCallback(() => {
    const parsed = Number.parseInt(jumpDraft, 10);
    if (Number.isNaN(parsed)) {
      setJumpDraft(String(selectedIndex + 1));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), repetitions.length);
    const next = clamped - 1;
    if (next !== selectedIndex) {
      onSelect(next);
    } else {
      setJumpDraft(String(selectedIndex + 1));
    }
  }, [jumpDraft, repetitions.length, selectedIndex, onSelect]);

  if (isLoading) {
    return (
      <div className={mergeClasses(styles.root, styles.rootAccent)} aria-busy="true">
        <div className={styles.head}>
          <span className={styles.headLabel}>
            <ArrowRepeatAllRegular className={styles.headLabelIcon} aria-hidden />
            Iteration
          </span>
        </div>
        <div className={styles.loading}>
          <Spinner size="extra-tiny" />
          <Text>Loading iterations…</Text>
        </div>
      </div>
    );
  }

  if (repetitions.length === 0) {
    return null;
  }

  const totalStr = totalDuration !== undefined ? formatDuration(totalDuration, { style: 'execution', fallback: '' }) : undefined;

  return (
    <div className={mergeClasses(styles.root, styles.rootAccent)}>
      <div className={styles.head}>
        <span className={styles.headLabel}>
          <ArrowRepeatAllRegular className={styles.headLabelIcon} aria-hidden />
          Iteration
        </span>
        <span className={styles.headMeta}>
          {totalStr ? `Total: ${totalStr} · ` : ''}
          {repetitions.length} {repetitions.length === 1 ? 'iteration' : 'iterations'}
        </span>
      </div>
      <div className={styles.row}>
        <div
          ref={railRef}
          className={styles.rail}
          role="tablist"
          aria-label="Iterations"
          onKeyDown={handleKeyDown}
        >
          {repetitions.map((rep, i) => {
            const isActive = i === selectedIndex;
            const kind = getStatusKind(rep.status);
            const isFailed = kind === 'failed';
            const cardClass = mergeClasses(
              styles.card,
              isCompact && styles.cardCompact,
              !isActive && isFailed && styles.cardFailed,
              isActive && styles.cardSelected,
            );
            const statusClass = mergeClasses(
              styles.cardStatus,
              kind === 'succeeded' && styles.cardStatusSucceeded,
              kind === 'failed' && styles.cardStatusFailed,
              kind === 'running' && styles.cardStatusRunning,
              kind === 'skipped' && styles.cardStatusSkipped,
              kind === 'pending' && styles.cardStatusPending,
              isActive && styles.cardStatusOnSelected,
            );
            const dotClass = mergeClasses(
              styles.cardCompactDot,
              kind === 'succeeded' && styles.cardCompactDotSucceeded,
              kind === 'failed' && styles.cardCompactDotFailed,
              kind === 'running' && styles.cardCompactDotRunning,
              kind === 'skipped' && styles.cardCompactDotSkipped,
              kind === 'pending' && styles.cardCompactDotPending,
              isActive && styles.cardCompactDotOnSelected,
            );
            const StatusIconCmp =
              kind === 'succeeded' ? CheckmarkCircleFilled
                : kind === 'failed' ? DismissCircleFilled
                : kind === 'running' ? ArrowSyncFilled
                : kind === 'skipped' ? SubtractCircleFilled
                : CircleHintFilled;
            const dur = formatDuration(rep.duration, { style: 'execution', fallback: '' });
            const ariaLabel = `Iteration ${rep.index + 1}${dur ? `, ${dur}` : ''}${STATUS_LABEL_SUFFIX[kind]}`;

            if (isCompact) {
              return (
                <button
                  key={rep.name}
                  role="tab"
                  type="button"
                  className={cardClass}
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  data-iter-selected={isActive ? 'true' : undefined}
                  aria-label={ariaLabel}
                  onClick={() => onSelect(i)}
                >
                  <span className={styles.cardCompactInner}>
                    <span className={styles.cardCompactIndex}>{rep.index + 1}</span>
                    <span className={dotClass} aria-hidden />
                  </span>
                </button>
              );
            }

            return (
              <button
                key={rep.name}
                role="tab"
                type="button"
                className={cardClass}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                data-iter-selected={isActive ? 'true' : undefined}
                aria-label={ariaLabel}
                onClick={() => onSelect(i)}
              >
                <span className={styles.cardTop}>
                  <span className={styles.cardIndex}>{rep.index + 1}</span>
                  <span className={statusClass} aria-hidden>
                    <StatusIconCmp />
                  </span>
                </span>
                {dur && (
                  <span
                    className={mergeClasses(
                      styles.cardDuration,
                      isActive && styles.cardDurationOnSelected,
                    )}
                  >
                    {dur}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className={styles.pager} role="group" aria-label="Iteration pager">
          <button
            type="button"
            className={styles.pagerBtn}
            onClick={goPrev}
            disabled={selectedIndex <= 0}
            aria-label="Previous iteration"
          >
            <ChevronLeftRegular />
          </button>
          {isCompact ? (
            <input
              ref={jumpInputRef}
              type="number"
              min={1}
              max={repetitions.length}
              value={jumpDraft}
              onChange={(e) => setJumpDraft(e.currentTarget.value)}
              onBlur={commitJump}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitJump();
                }
              }}
              className={styles.pagerJump}
              aria-label={`Jump to iteration. Currently ${selectedIndex + 1} of ${repetitions.length}`}
            />
          ) : (
            <span className={styles.pagerPos}>
              {selectedIndex + 1} of {repetitions.length}
            </span>
          )}
          {isCompact && (
            <span className={styles.pagerPos} aria-hidden>
              of {repetitions.length}
            </span>
          )}
          <button
            type="button"
            className={styles.pagerBtn}
            onClick={goNext}
            disabled={selectedIndex >= repetitions.length - 1}
            aria-label="Next iteration"
          >
            <ChevronRightRegular />
          </button>
        </div>
      </div>
    </div>
  );
};
